import { Injectable, inject } from '@angular/core';
import { Resolve, Router, Routes } from '@angular/router';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { CustomerModel } from '@df/session/api/customer.model';
import { Customer } from '@df/session/customer/customer.interfaces';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import flatten from 'lodash-es/flatten';
import { Adyen } from './adyen.namespace';

@Injectable({
    providedIn: 'root'
})
export class AdyenService {
    private _isGooglePayAvailable = true;

    set isGooglePayAvailable(value: boolean) {
        const was = this.isGooglePayAvailable;
        this._isGooglePayAvailable = value;
        if (was !== value) {
            this.app.events.broadcast(Checkout.EVENTS.RefreshPaymentMethods);
        }
    }

    get isGooglePayAvailable(): boolean {
        return this._isGooglePayAvailable;
    }

    /**
     * Adyen 3DS verification is in progress
     * @type {boolean}
     */
    private _is3DSInProgress = false;

    constructor(private app: App, private customer: CustomerModel, protected router: Router, private request: RequestService) {
        this.app.events
            .on(Checkout.EVENTS.PaymentTokensBefore)
            .subscribe((eventData: Checkout.IPaymentTokensBeforeEvent) => this.onCheckoutPaymentTokensInit(eventData));

        this.app.events.on(Checkout.EVENTS.Destroy).subscribe(() => {
            this.isGooglePayAvailable = false;
        });
        this.app.events.on(Checkout.EVENTS.SetPaymentMethodsBefore).subscribe((methods: Checkout.IPaymentMethod[]) => {
            if (!this.isGooglePayAvailable && methods?.length) {
                const index = findIndex(methods, method => {
                    const code = method.code;
                    if (typeof code === 'string') {
                        return code === Adyen.PaymentMethod.GooglePay;
                    }
                });
                if (index > -1) {
                    methods.splice(index, 1);
                }
            }
        });
    }

    ////////////////////////////////////////////
    ////////
    ////////    Script methods
    ////////
    ////////////////////////////////////////////

    /**
     * Returns js script url
     * @param {boolean} isTest
     * @returns {string}
     */
    getScriptName(isTest: boolean): string {
        return this.getLoadingContext(isTest) + Adyen.Assets.JS;
    }

    /**
     * Returns loading context
     * @param {boolean} isTest
     * @returns {string}
     */
    protected getLoadingContext(isTest: boolean): string {
        return isTest ? Adyen.LoadingContext.TEST : Adyen.LoadingContext.LIVE;
    }

    /**
     * Returns css url
     * @param {boolean} isTest
     * @returns {string}
     */
    getCssName(isTest: boolean): string {
        return this.getLoadingContext(isTest) + Adyen.Assets.CSS;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen checkout methods
    ////////
    ////////////////////////////////////////////

    /**
     * Returns new adyen checkout object
     * @param {string} key
     * @param {boolean} isTest
     * @returns {Adyen.ICheckout}
     */
    getCreateAdyenCheckout(configuration: Adyen.IConfiguration): Adyen.ICheckout | undefined {
        return typeof window === 'undefined' ? undefined : window.AdyenCheckout && new window.AdyenCheckout(configuration);
    }

    /**
     * Configuration object for adyen checkout components
     * @param {string} key
     * @param {boolean} isTest
     * @returns {Adyen.IConfiguration}
     */
    getAdyenConfiguration(key: string, isTest: boolean): Adyen.IConfiguration {
        return {
            locale: this.app.data.locale || 'en_GB',
            originKey: key,
            loadingContext: this.getLoadingContext(isTest)
        };
    }

    /**
     * Returns browser info needed by adyen components
     * @returns {Adyen.IBrowserInfo}
     */
    getBrowserInfo(): Adyen.IBrowserInfo {
        const screenWidth = (typeof window !== 'undefined' && window.screen?.width) || '';
        const screenHeight = (typeof window !== 'undefined' && window.screen.height) || '';
        const screenColorDepth = (typeof window !== 'undefined' && window.screen.colorDepth) || '';
        const language = (typeof window !== 'undefined' && (window.navigator.language || window.navigator['browserLanguage'])) || '';
        const timezoneOffset = new Date().getTimezoneOffset();

        return {
            screen_width: screenWidth,
            screen_height: screenHeight,
            screen_color_depth: screenColorDepth,
            java_enabled: false,
            timezone_offset: timezoneOffset,
            language
        };
    }

    ////////////////////////////////////////////
    ////////
    ////////    3DS methods
    ////////
    ////////////////////////////////////////////

    /**
     * If customer is in progress of 3ds verification getter
     * @returns {boolean}
     */
    get is3DSInProgress(): boolean {
        return this._is3DSInProgress;
    }

    /**
     * If customer is in progress of 3ds verification setter
     * @param {boolean} value
     */
    set is3DSInProgress(value: boolean) {
        this._is3DSInProgress = value;
        if (value) {
            this.setCancel3DSResolves();
        } else {
            this.removeCancel3DSResolves();
        }
    }

    /**
     * Sends adyen payment request
     * @param {Status} status
     * @param {Adyen.ICcData} data
     * @param {IRequestCallback} onSuccess
     * @param {IRequestCallback} onError
     * @returns {Promise<Adyen.IPayment>}
     */
    processPayment(
        status: Status | undefined,
        data: Adyen.ICcData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Adyen.IPayment> {
        return this.request.send(status, { path: 'adyen/payment', data }, onSuccess, onError);
    }

    /**
     * Sends adyen threeDS2 request
     * @param {Status | undefined} status
     * @param {Adyen.IThreeDS2ComponentResultData} data
     * @param {IRequestCallback} onSuccess
     * @param {IRequestCallback} onError
     * @returns {Promise<Adyen.IPayment>}
     */
    processThreeDS2(
        status: Status | undefined,
        data?: Adyen.IThreeDS2ComponentResultData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Adyen.IPayment> {
        return this.request.send(status, { path: 'adyen/threeDS2Process', data }, onSuccess, onError);
    }

    /**
     * Cancels threeDS process
     * in v3 we create order (quote is lost) and after that we do threeDS autorization
     * if threeds is not proceed we want to delete order and restore quote
     * @param {Status} status
     * @param {IRequestCallback} onSuccess
     * @param {IRequestCallback} onError
     * @returns {Promise<Customer.ILoadResponse>}
     */
    cancelThreeDS(
        status: Status | undefined,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback,
        alwaysCancel = false
    ): Promise<Customer.ILoadResponse> {
        return this.request.send(
            status,
            { path: 'adyen/threeDSCancel' },
            compact(
                flatten([
                    () => {
                        this.is3DSInProgress = false;
                    },
                    onSuccess
                ])
            ),
            compact(
                flatten([
                    () => {
                        if (alwaysCancel) {
                            this.is3DSInProgress = false;
                        }
                    },
                    onError
                ])
            )
        );
    }

    /**
     * Adds cancel3DS resolve to every route except checkout
     * we need to cancel 3ds verification when leaving checkout to restore quote
     */
    protected setCancel3DSResolves(): void {
        if (!this.isCancel3DSResolvesAdded()) {
            this.getRoutes().forEach(route => {
                if (!route.resolve) {
                    route.resolve = {};
                }
                route.resolve[Adyen.cancel3DSResolveName] = AdyenCancel3DSResolver;
            });
        }
    }

    /**
     * Removes cancel3DS resolve from every rout
     */
    protected removeCancel3DSResolves(): void {
        if (this.isCancel3DSResolvesAdded()) {
            this.getRoutes().forEach(route => {
                if (route.resolve && route.resolve[Adyen.cancel3DSResolveName]) {
                    delete route.resolve[Adyen.cancel3DSResolveName];
                }
            });
        }
    }

    /**
     * Returns all registered states
     * @returns {ng.ui.IState}
     */
    protected getRoutes(): Routes {
        return this.router.config;
    }

    /**
     * Returns if cancel3DS resolve is added to any state
     * @returns {boolean}
     */
    protected isCancel3DSResolvesAdded(): boolean {
        return !!this.getRoutes().find(route => route.resolve && route.resolve[Adyen.cancel3DSResolveName]);
    }

    ////////////////////////////////////////////
    ////////
    ////////    Oneclick methods
    ////////
    ////////////////////////////////////////////

    /**
     * filter out oneclick cards if there is no oneclick payment method available
     * @param {Checkout.IPaymentTokensBeforeEvent} eventData
     */
    protected onCheckoutPaymentTokensInit(eventData: Checkout.IPaymentTokensBeforeEvent): void {
        if (eventData) {
            const { tokens, methods } = eventData;
            const oneClickMethod = find(methods || [], method => method.code === Adyen.PaymentMethod.Oneclick);
            if (tokens?.length && !oneClickMethod) {
                eventData.tokens = tokens.filter(token => !token.method || token.method !== Adyen.PaymentMethod.Oneclick);
            }
        }
    }
}

/**
 * We need adyen cancel 3ds resolver in the same file as adyen service because of circular dependency
 */

@Injectable({
    providedIn: 'root'
})
export class AdyenCancel3DSResolver implements Resolve<Customer.ILoadResponse> {
    protected adyenService = inject(AdyenService);

    resolve(): Promise<Customer.ILoadResponse> {
        return this.adyenService.cancelThreeDS(undefined, undefined, undefined, true);
    }
}